import { Text } from '@blueprintjs/core'
import { GradientBorder } from 'client/components/GradientBorder/GradientBorder'
import { ResponsiveBlock } from 'client/components/ResponsiveBlock/ResponsiveBlock'
import { ClientLocaleNS } from 'lib/constants/locales'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'client/components/Button/Button'
import { useFormik } from 'formik'
import { SIGNUP_FORM_SCHEMA } from 'lib/constants/validation'
import cn from 'classnames'
import {
  FormField,
  TFormFieldProps,
} from 'client/contents/CheckoutPageContent/components/OrderForm/components/FormField/FormField'
import { router, usePage } from '@inertiajs/react'

type TProps = {}

type TPageProps = {
  alert?: string
  notice?: string
  user_errors?: any
}

type TSignupForm = {
  first_name: string
  last_name: string
  email_address: string
  password: string
  password_confirmation: string
}
type TSignupFormFieldKey = keyof TSignupForm
type TSubmitResult = {
  text: string
  status: 'success' | 'failure'
} | null

const INITIAL_VALUES: TSignupForm = {
  first_name: '',
  last_name: '',
  email_address: '',
  password: '',
  password_confirmation: '',
}

const SignUpForm: FC<TProps> = () => {
  const { t } = useTranslation(ClientLocaleNS.SignUpPage)
  const {
    props: { alert, notice, user_errors },
  } = usePage<TPageProps>()
  const [submitResult, setSubmitResult] = useState<TSubmitResult>(null)
  const { handleChange, handleSubmit, errors, values, setErrors } = useFormik<TSignupForm>({
    initialValues: INITIAL_VALUES,
    initialErrors: user_errors ?? {},
    validationSchema: SIGNUP_FORM_SCHEMA,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: (values, { validateForm }) => {
      validateForm(values).then(() => {
        router.visit('/sign_up', {
          method: 'post',
          data: {
            user: {
              ...values,
            },
          },
        })
      })
    },
  })

  useEffect(() => {
    if (alert) {
      setSubmitResult({ text: alert, status: 'failure' })
    }
    if (notice) {
      setSubmitResult({ text: notice, status: 'success' })
    }
    if (user_errors) {
      setErrors(user_errors)
    }
  }, [alert, notice, user_errors])

  const getFormFieldProps = (key: TSignupFormFieldKey): TFormFieldProps => ({
    label: t(`form.fields.${key}.title`),
    name: key,
    id: key,
    type: 'text',
    value: values[key],
    onChange: handleChange,
    error: errors[key],
  })

  return (
    <ResponsiveBlock className="py-30" contentClassName="justify-center">
      <GradientBorder>
        <form className="flex flex-1 flex-col p-12 gap-3" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-12">
            <div className="flex flex-col w-full gap-2">
              <Text tagName="span" className="font-body---20px">
                {t('title')}
              </Text>
            </div>
            <FormField {...getFormFieldProps('first_name')} />
            <FormField {...getFormFieldProps('last_name')} />
            <FormField {...getFormFieldProps('email_address')} />
            <FormField {...getFormFieldProps('password')} type="password" />
            <FormField {...getFormFieldProps('password_confirmation')} type="password" />
          </div>
          <div className="flex w-full flex-col items-center lg:items-start">
            <div className="flex w-full flex-1 items-center justify-center lg:justify-start">
              <Text
                tagName="span"
                className={cn(
                  'font-primary-regular---15pt flex transition-opacity py-2',
                  !!submitResult ? 'opacity-100' : 'opacity-0',
                  {
                    'text-green-500': submitResult?.status === 'success',
                    'text-palette-red': submitResult?.status === 'failure',
                  },
                )}
              >
                {submitResult?.text ?? ''}
              </Text>
            </div>
            <div className="flex flex-col w-full">
              <Button type="submit" className="h-12">
                <Text tagName="span">{t('form.button')}</Text>
              </Button>
            </div>
          </div>
        </form>
      </GradientBorder>
    </ResponsiveBlock>
  )
}

export { SignUpForm }
